// bootstrap-axios.js
import axios from 'axios'

// globals
axios.defaults.headers.common['Accept'] = 'application/json'
axios.defaults.timeout = 10_000
axios.defaults.withCredentials = true

const MAX_RETRIES = 4
const MAX_BACKOFF = 15_000
const BASE_BACKOFF = 300

function jitter(n) {
	return Math.random() * n
}
function backoff(n) {
	return Math.min(BASE_BACKOFF * 2 ** n + jitter(200), MAX_BACKOFF)
}

function isTransient(err) {
	const s = err?.response?.status
	return (
		!err?.response ||
		s === 408 ||
		s === 429 ||
		s >= 500 ||
		err?.code === 'ECONNABORTED'
	)
}
function axErr(message, code, cfg) {
	const e = new Error(message)
	e.isAxiosError = true
	e.code = code
	e.config = cfg
	return e
}

function genKey() {
	if (typeof crypto !== 'undefined' && crypto.randomUUID)
		return crypto.randomUUID()
	return Date.now().toString(36) + Math.random().toString(36).slice(2, 10)
}

// sleep that aborts with the request's AbortSignal
function sleep(ms, signal) {
	if (!ms) return Promise.resolve()
	return new Promise((resolve, reject) => {
		const t = setTimeout(done, ms)
		function onAbort() {
			cleanup()
			reject(axErr('canceled', 'ERR_CANCELED'))
		}
		function done() {
			cleanup()
			resolve()
		}
		function cleanup() {
			clearTimeout(t)
			if (signal) signal.removeEventListener?.('abort', onAbort)
		}
		if (signal) {
			if (signal.aborted) return onAbort()
			signal.addEventListener?.('abort', onAbort, { once: true })
		}
	})
}

// wait for navigator.onLine then replay THIS request; respects AbortSignal
function waitForOnlineThenRequest(cfg) {
	return new Promise((resolve, reject) => {
		function onAbort() {
			cleanup()
			reject(axErr('canceled', 'ERR_CANCELED', cfg))
		}
		function onOnline() {
			cleanup()
			resolve(axios.request(cfg))
		} // <- chain to the same promise
		function cleanup() {
			window.removeEventListener('online', onOnline)
			if (cfg.signal) cfg.signal.removeEventListener?.('abort', onAbort)
		}
		if (cfg.signal) {
			if (cfg.signal.aborted) return onAbort()
			cfg.signal.addEventListener?.('abort', onAbort, { once: true })
		}
		if (navigator.onLine) return resolve(axios.request(cfg))
		window.addEventListener('online', onOnline, { once: true })
	})
}

// REQUEST interceptor
axios.interceptors.request.use((cfg = {}) => {
	const method = (cfg.method || 'get').toLowerCase()

	// add idempotency key for non-GET so retries are safe server-side
	if (!['get', 'head', 'options'].includes(method)) {
		cfg.headers = cfg.headers || {}
		if (!cfg.headers['Idempotency-Key'])
			cfg.headers['Idempotency-Key'] = genKey()
	}

	// if offline, do NOT reject; return a promise that waits and then replays THIS request
	if (typeof navigator !== 'undefined' && !navigator.onLine) {
		return waitForOnlineThenRequest(cfg)
	}

	// init counter
	if (cfg.__retryCount == null) cfg.__retryCount = 0
	return cfg
})

// RESPONSE interceptor
axios.interceptors.response.use(
	(res) => res,
	async (err) => {
		const cfg = err?.config || {}
		// if offline after failure, wait, then replay this same request promise
		if (typeof navigator !== 'undefined' && !navigator.onLine) {
			return waitForOnlineThenRequest(cfg)
		}

		if (!isTransient(err)) throw err

		cfg.__retryCount = (cfg.__retryCount || 0) + 1
		if (cfg.__retryCount > MAX_RETRIES) {
			// If all retries failed and we haven't already refreshed, force a refresh
			const url = new URL(window.location.href)
			if (!url.searchParams.has('retryRefresh')) {
				url.searchParams.set('retryRefresh', '1')
				window.location.href = url.toString()
				// Return a never-resolving promise to prevent error handling while refreshing
				return new Promise(() => {})
			}
			throw err
		}

		await sleep(backoff(cfg.__retryCount), cfg.signal)
		return axios.request(cfg) // return the chained retry promise
	}
)
